import {
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { TranslocoDirective } from '@ngneat/transloco';
import {
  IconAlertComponent,
  IconBackComponent,
  IconBanComponent,
  IconEyeComponent,
  IconPrintComponent,
} from '@pedix-workspace/angular-ui-icons';
import { ActionSheetComponent } from '@pedix-workspace/angular-ui-action-sheet';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import {
  getArcaVoucherTypeName,
  isTaxVoucherArcaSummary,
} from '@pedix-workspace/integrations-arca';
import { saveAs } from 'file-saver';
import { PdfModalViewerComponent } from '@pedix-workspace/angular-ui-pdf';
import { ModalDialogComponent } from '@pedix-workspace/angular-ui-modal';
import { ToastrService } from 'ngx-toastr';
import { TaxVoucherActionsService, TaxVoucherInput } from './tax-voucher-actions.service';

@Component({
  selector: 'app-tax-voucher-actions',
  templateUrl: './tax-voucher-actions.component.html',
  styleUrls: ['./tax-voucher-actions.component.scss'],
  standalone: true,
  imports: [
    TranslocoDirective,
    ActionSheetComponent,
    ButtonComponent,
    IconBackComponent,
    IconEyeComponent,
    IconPrintComponent,
    IconBanComponent,
    PdfModalViewerComponent,
    ModalDialogComponent,
    IconAlertComponent,
  ],
})
export class TaxVoucherActionsComponent {
  private toastrService = inject(ToastrService);
  private taxVoucherActionsService = inject(TaxVoucherActionsService);

  readonly confirmCreditNoteDialog =
    viewChild.required<ModalDialogComponent>('confirmCreditNoteDialog');

  taxVoucherInput = input.required<TaxVoucherInput>();
  displayCreditNoteAction = input.required<boolean>();
  loading = model<boolean>();

  displayTaxVoucherType = input<boolean>(false);

  pdfPreviewSrc = signal<string | null>(null);
  printing = signal(false);
  generatingCreditNote = signal(false);
  printingTaxVoucherTicket = signal(false);
  downloading = signal(false);

  readonly taxVoucherType = computed(() => {
    const taxVoucherInput = this.taxVoucherInput();

    if (isTaxVoucherArcaSummary(taxVoucherInput)) {
      return getArcaVoucherTypeName(taxVoucherInput.details.type) || '--';
    }

    return taxVoucherInput.details.type || '--';
  });

  constructor() {
    effect(
      () => {
        const isLoading = this.printing() || this.generatingCreditNote() || this.downloading();

        this.loading.set(isLoading);
      },
      {
        allowSignalWrites: true,
      },
    );
  }

  async onViewTaxVoucher(taxVoucherInput: TaxVoucherInput) {
    const pdfUrl = this.taxVoucherActionsService.getPdfUrl(taxVoucherInput);

    this.pdfPreviewSrc.set(pdfUrl);
  }

  async onDownloadTaxVoucher(taxVoucherInput: TaxVoucherInput) {
    try {
      this.downloading.set(true);

      const pdfUrl = this.taxVoucherActionsService.getPdfUrl(taxVoucherInput);
      const fileName = `${this.taxVoucherType()} - ${this.taxVoucherInput().details.voucherRef}.pdf`;

      // Descarga asíncrona para evitar bloqueo de UI
      await this.downloadFileAsync(pdfUrl, fileName);
    } catch (error) {
      this.toastrService.error('No se pudo descargar el comprobante');

      console.error('Download error:', error);
    } finally {
      this.downloading.set(false);
    }
  }

  /**
   * Descarga un archivo de forma asíncrona usando fetch para evitar bloqueo de UI
   * @param url URL del archivo a descargar
   * @param fileName Nombre del archivo a guardar
   */
  private async downloadFileAsync(url: string, fileName: string): Promise<void> {
    // Usar fetch para descarga no bloqueante
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Obtener el blob de forma asíncrona
    const blob = await response.blob();

    // Usar saveAs con el blob ya descargado (operación instantánea)
    saveAs(blob, fileName);
  }

  async onPrintTaxVoucher(taxVoucherInput: TaxVoucherInput) {
    const pdfUrl = this.taxVoucherActionsService.getPdfUrl(taxVoucherInput);

    this.printing.set(true);

    // NOTE: this import breaks SSR, so we need to import it dynamically
    const { default: printJS } = await import('print-js');

    printJS({
      printable: pdfUrl,
      type: 'pdf',
      onLoadingEnd: () => {
        this.printing.set(false);
      },
    });
  }

  async onPrintTaxVoucherTicket(taxVoucherInput: TaxVoucherInput) {
    this.printingTaxVoucherTicket.set(true);

    try {
      this.taxVoucherActionsService.onPrintTaxVoucherTicket(taxVoucherInput);
    } catch (error) {
      this.toastrService.error('Error al imprimir el comprobante');

      console.error(error);
    } finally {
      this.printingTaxVoucherTicket.set(false);
    }
  }

  async onGenerateCreditNote() {
    try {
      await this.confirmCreditNoteDialog().open();

      this.generatingCreditNote.set(true);

      await this.taxVoucherActionsService.onGenerateCreditNote(this.taxVoucherInput());
    } catch (error) {
      // canceled
    } finally {
      this.generatingCreditNote.set(false);
    }
  }

  onError(error: any) {
    this.toastrService.error('Error al visualizar el comprobante');

    console.error(error);
  }
}
